import i18n from '../i18n'
import { errorCode } from '../services/api'
import { daysAgo } from './archive'
import { faDigits } from './numerals'

/**
 * The feed's rules on the client (design page 10, `feed`; P-feed), mirrored
 * from the Android app's `SocialRepository.kt` / `FeedScreen.kt` so both
 * clients change a card the same way and say the same sentence for the
 * same refusal. The server decides everything; this file only shapes.
 * Plain functions, not components, so they read `i18n.t()` directly rather
 * than the `useTranslation()` hook — the `social` namespace.
 */

const t = (key, options) => i18n.t(key, { ns: 'social', ...options })

/**
 * Functions, not constants: the active language decides the labels.
 * `teammates` (V5-w) puts «هم‌تیمی‌ها» where «دنبال‌شده‌ها» was — only for a
 * learner with a team (`hasTeammatesTab` in lib/teams.js); everyone else,
 * and everyone while teams are off, keeps today's three tabs.
 */
export const SCOPES = (teammates = false) => [
  teammates ? { key: 'teammates', label: t('scopes.teammates') } : { key: 'following', label: t('scopes.following') },
  { key: 'top', label: t('scopes.top') },
  { key: 'new', label: t('scopes.new') },
]

export const PERIODS = () => [
  { key: 'weekly', label: t('periods.weekly') },
  { key: 'monthly', label: t('periods.monthly') },
]

/** The server's cap on a comment; the field stops there rather than letting the server refuse. */
export const COMMENT_MAX_LENGTH = 280

/** «امروز» / «دیروز» / «N روز پیش» for a post's instant. */
export function whenLabel(isoInstant, now = new Date()) {
  const days = daysAgo(isoInstant, now)
  if (days === null) return ''
  if (days === 0) return t('whenLabel.today')
  if (days === 1) return t('whenLabel.yesterday')
  return t('whenLabel.daysAgo', { count: faDigits(days) })
}

/** A mean like 4.6 in Persian digits with the Persian decimal separator, as the design writes «۴٫۶». */
export function faDecimal(value) {
  return value
    .toFixed(1)
    .split('')
    .map((c) => (c === '.' ? '٫' : faDigits(c)))
    .join('')
}

/**
 * Whether the stars on a card take a tap (owner rule 2026-10-14, «تا آخر
 * پخش کرده»): never on one's own post; otherwise only once its recording has
 * played to the end for this learner (`heard`, lib/heardPosts.js) — or when
 * they rated it before, so an earlier vote can still be changed. The server
 * enforces its own side (`listen_first`).
 */
export function canRate(post, heard) {
  if (!post || post.author?.me) return false
  return heard === true || (post.myRating ?? 0) > 0
}

/** Whether the clap takes a tap: one's own post, a post heard to the end, or a clap to take back. */
export function canClap(post, heard) {
  if (!post) return false
  if (post.author?.me || post.clappedByMe) return true
  return heard === true
}

/** A card after the server answered a reaction on it — the counters the answer carries, nothing guessed. */
export function withClap(post, result) {
  return { ...post, claps: result.claps, clappedByMe: result.clapped }
}

export function withRating(post, result) {
  return { ...post, myRating: result.myRating, averageRating: result.averageRating, ratingCount: result.ratingCount }
}

export function withComment(post, comment) {
  return { ...post, comments: [...(post.comments ?? []), comment], commentCount: (post.commentCount ?? 0) + 1 }
}

/** Every card of `userId` learns the new follow state at once, including the comments they left. */
export function withFollow(posts, userId, following) {
  const mark = (author) => (author.id === userId ? { ...author, followedByMe: following } : author)
  return posts.map((post) => ({
    ...post,
    author: mark(post.author),
    comments: (post.comments ?? []).map((comment) => ({ ...comment, author: mark(comment.author) })),
  }))
}

export function replacePost(posts, next) {
  return posts.map((post) => (post.id === next.id ? next : post))
}

export function removePost(posts, postId) {
  return posts.filter((post) => post.id !== postId)
}

/**
 * The sentence for why the server refused a reaction or a share, read by
 * what was asked (`share` · `clap` · `rate` · `comment` · `follow`). A
 * request that never reached the server is offline, not a refusal.
 * `consent_required` is not a sentence: the caller opens the consent step.
 */
export function refusalFor(error, kind) {
  if (!error?.response) return t('refusals.offline')
  switch (errorCode(error)) {
    case 'consent_required':
      return null
    case 'attempt_not_shareable':
      return t('refusals.notShareable')
    case 'attempt_not_found':
      return t('refusals.attemptNotFound')
    case 'own_post':
      return t('refusals.ownPost')
    case 'self_follow':
      return t('refusals.selfFollow')
    case 'post_not_found':
      return t('refusals.postNotFound')
    case 'comment_empty':
      return t('refusals.commentEmpty')
    case 'listen_first':
      return t('refusals.listenFirst')
    default:
      return kind === 'share' ? t('refusals.shareDefault') : t('refusals.default')
  }
}

/** Whether the server's answer to a share means "ask for consent first". */
export function needsConsent(error) {
  return errorCode(error) === 'consent_required'
}
